import axios from "axios";

import { getSessionAccessToken, refreshSessionTokens } from "../services/sessionToken";

type TimedRequestConfig = {
  metadata?: {
    startedAt: number;
    correlationId: string;
  };
  _retryAuth?: boolean;
};

const debugTiming = process.env.REACT_APP_DEBUG_API_TIMING === "true";

const bearerTokenFromAuthorization = ( authorization?: unknown ): string | undefined => {
  if( typeof authorization !== "string" ) return undefined;

  const parts = authorization.trim().split(/\s+/);
  if( parts.length === 2 && /^Bearer$/i.test( parts[ 0 ])) return parts[ 1 ];
  return undefined;
};

const getHeaderValue = ( headers: any, name: string ): unknown => {
  if( !headers ) return undefined;
  if( typeof headers.get === "function" ) return headers.get( name ) ?? headers.get( name.toLowerCase());
  return headers[ name ] ?? headers[ name.toLowerCase() ];
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const correlationId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  config.headers = config.headers ?? {};
  let authorization = config.headers["Authorization"];
  const sessionAccessToken = getSessionAccessToken();
  if (typeof authorization === "string" && /^Bearer\s*(undefined|null)?\s*$/i.test(authorization)) {
    delete config.headers["Authorization"];
    authorization = undefined;
  }
  if( !authorization && sessionAccessToken ) {
    config.headers["Authorization"] = `Bearer ${ sessionAccessToken }`;
    authorization = config.headers["Authorization"];
  }
  config.headers["X-Correlation-ID"] = correlationId;
  (config as typeof config & TimedRequestConfig).metadata = {
    startedAt: Date.now(),
    correlationId,
  };

  return config;
});

api.interceptors.response.use(
  (response) => {
    if (debugTiming) {
      const config = response.config as typeof response.config & TimedRequestConfig;
      const startedAt = config.metadata?.startedAt ?? Date.now();
      // eslint-disable-next-line no-console
      console.debug("[api]", {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        duration_ms: Date.now() - startedAt,
        correlation_id:
          response.headers["x-correlation-id"] ?? config.metadata?.correlationId,
      });
    }

    return response;
  },
  async (error) => {
    if (debugTiming && error.config) {
      const config = error.config as typeof error.config & TimedRequestConfig;
      const startedAt = config.metadata?.startedAt ?? Date.now();
      // eslint-disable-next-line no-console
      console.debug("[api:error]", {
        method: error.config.method?.toUpperCase(),
        url: error.config.url,
        status: error.response?.status,
        duration_ms: Date.now() - startedAt,
        correlation_id:
          error.response?.headers?.["x-correlation-id"] ??
          config.metadata?.correlationId,
      });
    }

    const config = error.config as typeof error.config & TimedRequestConfig | undefined;
    if( error.response?.status === 401 && config && !config._retryAuth ) {
      config._retryAuth = true;
      const failedAccessToken = bearerTokenFromAuthorization( getHeaderValue( config.headers, "Authorization" ));
      if( !failedAccessToken ) return Promise.reject(error);

      const currentAccessToken = getSessionAccessToken();
      if( failedAccessToken && currentAccessToken && failedAccessToken !== currentAccessToken ) {
        config.headers = config.headers ?? {};
        config.headers["Authorization"] = `Bearer ${ currentAccessToken }`;
        return api.request( config );
      }

      const refreshed = await refreshSessionTokens();
      config.headers = config.headers ?? {};
      config.headers["Authorization"] = `Bearer ${ refreshed.accessToken }`;
      return api.request( config );
    }

    return Promise.reject(error);
  }
);

export default api;
